/* eslint-disable spire/export-styles */
import getLocalizedCurrency from "@insite/client-framework/Common/Utilities/getLocalizedCurrency";
import { getUnitListPrice, getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { CurrencyPositioningType, LanguageModel } from "@insite/client-framework/Types/ApiModels";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { connect } from "react-redux";

/* XCentium customization to price labels and decimals */

type OwnProps = TypographyPresentationProps & {
    pricing: ProductPriceDto;
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    currencySymbol: string;
    qtyOrdered?: number;
    showOnlyPercentOfSavings?: boolean;
};

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ProductPriceSavingsMessage: FC<Props> = ({
    pricing,
    showSavingsAmount,
    showSavingsPercent,
    currencySymbol,
    qtyOrdered = 1,
    language,
    showOnlyPercentOfSavings = false,
    ...otherProps
}) => {
    const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered);
    const { price: unitListPrice, priceDisplay: unitListPriceDisplay } = getUnitListPrice(pricing, qtyOrdered);

    const impersonationMessage = pricing.additionalResults?.impersonatedDiscountDisplay;
    const hasPriceSavings = unitNetPrice < unitListPrice || impersonationMessage;
    if (!language || !pricing || !hasPriceSavings) {
        return null;
    }

    const savingsAmount = unitListPrice - unitNetPrice;
    const localizedSavingsAmount = xcGetLocalizedCurrency({ amount: savingsAmount, language, currencySymbol });
    const savingsPercent = Math.round((savingsAmount / unitListPrice) * 100);

    let message = "";
    const discount = pricing.additionalResults?.discount;
    if (impersonationMessage) {
        message = impersonationMessage;
    } else if (discount) {
        message = translate("Your Price: {0}, you save with {1}% web discount", unitListPriceDisplay, discount);
    } else if (showSavingsAmount && showSavingsPercent) {
        message = translate(
            "Your Price: {0}, you save {1} ({2}%)",
            unitListPriceDisplay,
            localizedSavingsAmount,
            `${savingsPercent}`,
        );
    } else if (showSavingsPercent) {
        message = translate(
            "Your Price: {0}, you save with {1}% web discount",
            unitListPriceDisplay,
            `${savingsPercent}`,
        );
    } else if (showSavingsAmount) {
        message = translate("Your Price: {0}, you save {1}", unitListPriceDisplay, localizedSavingsAmount);
    } else {
        message = translate("Your Price: {0}", unitListPriceDisplay);
    }

    if (showOnlyPercentOfSavings) {
        message = `Saving ${savingsPercent}%`;
    }

    return (
        <Typography {...otherProps} data-test-selector="productPriceSavingsMessage">
            {message}
        </Typography>
    );
};

interface CurrencyProps {
    amount: number;
    currencySymbol: string;
    language: LanguageModel;
}

const xcGetLocalizedCurrency = ({ amount, language, currencySymbol }: CurrencyProps) => {
    const locale = language.cultureCode || language.languageCode;
    const options = {
        style: "decimal",
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
    };
    let currencyFormatter;
    try {
        currencyFormatter = new Intl.NumberFormat(locale, options);
    } catch {
        currencyFormatter = new Intl.NumberFormat(undefined, options);
    }

    // format the same way as CurrencyFormatProvider in the .NET code
    const negativeSymbol = amount < 0 ? "-" : "";
    const amountStrWithoutNegativeSign = currencyFormatter.format(Math.abs(amount));

    return language.currencyPositioning === CurrencyPositioningType.Left
        ? `${negativeSymbol}${currencySymbol}${amountStrWithoutNegativeSign}`
        : `${negativeSymbol}${amountStrWithoutNegativeSign}${currencySymbol}`;
};

export default connect(mapStateToProps)(ProductPriceSavingsMessage);
